import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { startSession, closeActiveSession } from "@/data/sessionLogStore";
import { invalidateSessionCache } from "@/data/cloudSync";
import { loadPermissionConfig, hydratePermissionConfig } from "@/data/permissionsStore";

export type AppRole = "super_admin" | "admin" | "manager" | "staff" | "store_manager";

export interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  commission_percent: number;
  created_at: string;
  role: AppRole;
  phone_number?: string;
  profile_image_url?: string;
  location?: string;
}

export interface RolePermissions {
  canEditPricing: boolean;
  canEditStock: boolean;
  canChangeExchangeRate: boolean;
  canManageOrders: boolean;
  canManageTracking: boolean;
  canEditEnquiries: boolean;
  canGenerateInvoices: boolean;
  canViewAnalytics: boolean;
  canViewSalesAnalytics: boolean;
  canManageMedia: boolean;
  canManageDevices: boolean;
  canManageUsers: boolean;
  canDeleteUsers: boolean;
  canViewCommission: boolean;
  canManageSms: boolean;
  canCreateGroupChats: boolean;
  canChatWithUsers: boolean;
  canViewAllNotifications: boolean;
  canViewSyncStatus: boolean;
  canViewAllConversations: boolean;
  canManageCatalog: boolean;
}

/**
 * Role labels. Super Admin === Owner of the entire platform — distinct
 * from Admin, which is a delegated main-site role. Store Manager owns one
 * tenant storefront and is *never* a main-site Admin; label it explicitly
 * so the two cannot be confused in any UI surface.
 */
export const roleLabels: Record<AppRole, string> = {
  super_admin: "Super Admin (Owner)",
  admin: "Admin",
  manager: "Manager",
  staff: "Staff",
  store_manager: "Store Manager Admin",
};

export function getPermissions(role: AppRole | null): RolePermissions {
  if (!role) return getPermissions("staff");
  
  // Super admin always has everything
  if (role === "super_admin") {
    return {
      canEditPricing: true, canEditStock: true, canChangeExchangeRate: true,
      canManageOrders: true, canManageTracking: true, canEditEnquiries: true,
      canGenerateInvoices: true, canViewAnalytics: true,
      canViewSalesAnalytics: true, canManageMedia: true, canManageDevices: true,
      canManageUsers: true, canDeleteUsers: true, canViewCommission: true, canManageSms: true,
      canCreateGroupChats: true, canChatWithUsers: true, canViewAllNotifications: true,
      canViewSyncStatus: true, canViewAllConversations: true, canManageCatalog: true,
    };
  }
  if (role === "admin") {
    return {
      canEditPricing: true, canEditStock: true, canChangeExchangeRate: true,
      canManageOrders: true, canManageTracking: true, canEditEnquiries: true,
      canGenerateInvoices: true, canViewAnalytics: true,
      canViewSalesAnalytics: true, canManageMedia: true, canManageDevices: true,
      canManageUsers: true, canDeleteUsers: false, canViewCommission: true, canManageSms: true,
      canCreateGroupChats: true, canChatWithUsers: true, canViewAllNotifications: true,
      canViewSyncStatus: true, canViewAllConversations: true, canManageCatalog: true,
    };
  }
  // Store managers are full owners of their tenant scope but cannot manage
  // platform-wide users or see cross-tenant analytics.
  if (role === "store_manager") {
    return {
      canEditPricing: true, canEditStock: true, canChangeExchangeRate: true,
      canManageOrders: true, canManageTracking: true, canEditEnquiries: true,
      canGenerateInvoices: true, canViewAnalytics: true,
      canViewSalesAnalytics: true, canManageMedia: true, canManageDevices: true,
      canManageUsers: false, canDeleteUsers: false, canViewCommission: false, canManageSms: false,
      canCreateGroupChats: false, canChatWithUsers: false, canViewAllNotifications: false,
      canViewSyncStatus: true, canViewAllConversations: false, canManageCatalog: false,
    };
  }
  
  // Load custom permissions from store
  const config = loadPermissionConfig();
  const perms = config[role];
  
  return {
    canEditPricing: perms.canEditPricing ?? false,
    canEditStock: perms.canEditStock ?? false,
    canChangeExchangeRate: perms.canChangeExchangeRate ?? false,
    canManageOrders: perms.canManageOrders ?? true,
    canManageTracking: perms.canManageTracking ?? false,
    canEditEnquiries: perms.canEditEnquiries ?? false,
    canGenerateInvoices: perms.canGenerateInvoices ?? false,
    canViewAnalytics: perms.canViewAnalytics ?? false,
    canViewSalesAnalytics: perms.canViewSalesAnalytics ?? false,
    canManageMedia: perms.canManageMedia ?? false,
    canManageDevices: perms.canManageDevices ?? false,
    canManageUsers: perms.canManageUsers ?? false,
    canDeleteUsers: perms.canDeleteUsers ?? false,
    canViewCommission: perms.canViewCommission ?? true,
    canManageSms: perms.canManageSms ?? false,
    canCreateGroupChats: perms.canCreateGroupChats ?? false,
    canChatWithUsers: perms.canChatWithUsers ?? false,
    canViewAllNotifications: perms.canViewAllNotifications ?? false,
    canViewSyncStatus: perms.canViewSyncStatus ?? false,
    canViewAllConversations: perms.canViewAllConversations ?? false,
    canManageCatalog: perms.canManageCatalog ?? false,
  };
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();
      const role = (roleData?.role as AppRole) || null;

      // Phase 7 — Store Managers must be recognised as admin users even on
      // the very first post-signup render before the profile row is visible.
      // Fall back to a synthetic profile derived from user_metadata.
      if (!profileData && role === "store_manager") {
        const meta = (await supabase.auth.getUser()).data.user?.user_metadata as Record<string, unknown> | null;
        const synthetic = {
          id: userId,
          user_id: userId,
          username: String(meta?.store_title || meta?.full_name || "Store Manager"),
          commission_percent: 0,
          phone_number: String(meta?.phone_number || ""),
          location: (meta?.location as string) ?? null,
          profile_image_url: (meta?.profile_image_url as string) ?? null,
          notification_email: null,
          created_at: new Date().toISOString(),
        } as unknown as Parameters<typeof setProfile>[0];
        setProfile({ ...(synthetic as object), role: "store_manager" } as UserProfile);
        return;
      }

      // Customers have no row in `profiles` AND no user_roles row. Treat
      // absence as "not staff" so the admin dashboard never renders for a
      // customer login.
      if (!profileData) {
        setProfile(null);
        return;
      }

      const resolvedRole = (role as AppRole) || "staff";
      setProfile({
        ...profileData,
        commission_percent: Number(profileData.commission_percent),
        role: resolvedRole,
      });
      startSession(userId, profileData.username || userId, profileData.username || "Unknown", resolvedRole);
      if (resolvedRole === "super_admin" || resolvedRole === "admin" || resolvedRole === "manager") {
        hydratePermissionConfig().catch(() => {});
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setLoading(true);
          // Use setTimeout to avoid potential deadlocks with Supabase, but keep
          // auth loading active until the staff/customer profile check finishes.
          setTimeout(async () => {
            await fetchProfile(session.user.id);
            setLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Restore session on mount. We do NOT race against a hard timeout that
    // wipes the localStorage token: a slow network on refresh would otherwise
    // sign the user out even though their session is perfectly valid. The
    // onAuthStateChange listener above is already wired up and will hydrate
    // state as soon as supabase-js finishes its lock acquisition.
    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id);
        }
        setLoading(false);
      })
      .catch((err) => {
        // Real failure (not a timeout) — log it but don't nuke the token.
        console.warn("getSession failed:", err);
        setLoading(false);
      });

    // Track page leave
    const handleBeforeUnload = () => closeActiveSession("page_leave");
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [fetchProfile]);

  const signIn = async (email: string, password: string) => {
    // Clear any stale session token to prevent navigator lock contention
    try {
      const storageKey = `sb-jialddxkojvhbstbsnft-auth-token`;
      localStorage.removeItem(storageKey);
    } catch {}
    invalidateSessionCache();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error && data?.user) {
      // Phase A: identity audit trail
      (supabase as any).from("identity_audit_log").insert({
        actor_user_id: data.user.id,
        target_user_id: data.user.id,
        event_type: "login",
        metadata: { method: "password" },
      }).then(() => {}, () => {});
    }
    return { data, error };
  };

  const signUp = async (email: string, password: string, username: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username } },
    });
    return { data, error };
  };

  const signOut = async () => {
    // Best-effort logout activity log (immediate flush so it lands before the token clears).
    try {
      const { logCustomerActivity } = await import("@/data/customerActivity");
      await logCustomerActivity("logout", {}, { immediate: true });
    } catch {}
    if (user) {
      try {
        await (supabase as any).from("identity_audit_log").insert({
          actor_user_id: user.id, target_user_id: user.id, event_type: "logout", metadata: {},
        });
      } catch {}
    }
    closeActiveSession("manual");
    invalidateSessionCache();
    await supabase.auth.signOut();
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  };

  const refreshProfile = useCallback(() => {
    if (user) fetchProfile(user.id);
  }, [user, fetchProfile]);

  return {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
    permissions: getPermissions(profile?.role ?? null),
  };
}

// Admin functions
export async function getOwnerUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.from("app_data").select("data_value").eq("data_key", "khays_owner_id").maybeSingle();
    if (data?.data_value) {
      const val = typeof data.data_value === "string" ? JSON.parse(data.data_value) : data.data_value;
      return typeof val === "string" ? val : null;
    }
  } catch {}
  return null;
}

export async function getAllProfiles(): Promise<UserProfile[]> {
  const { data: profiles } = await supabase.from("profiles").select("*");
  const { data: roles } = await supabase.from("user_roles").select("*");
  // Pull all account requests so we can hide auth users that don't yet have
  // an approved request. A user is hidden ONLY if every request linked to
  // their auth_user_id is non-approved (pending/rejected). If any approved
  // request exists for them, they belong on the team list.
  const { data: allReqs } = await supabase
    .from("account_requests")
    .select("auth_user_id,status");

  if (!profiles) return [];

  // Get owner user_id to filter out
  const ownerUserId = await getOwnerUserId();

  const roleMap = new Map((roles || []).map(r => [r.user_id, r.role as AppRole]));
  const approvedAuthIds = new Set(
    (allReqs || []).filter(r => r.status === "approved" && r.auth_user_id).map(r => r.auth_user_id as string)
  );
  const reqAuthIds = new Set(
    (allReqs || []).map(r => r.auth_user_id).filter(Boolean) as string[]
  );
  // Hide profiles only when they appear in account_requests AND have no approved row.
  const unapprovedIds = new Set(
    [...reqAuthIds].filter(id => !approvedAuthIds.has(id))
  );

  return profiles
    .filter(p => p.user_id !== ownerUserId && p.username !== "__owner__")
    .filter(p => !unapprovedIds.has(p.user_id))
    .map(p => ({
      ...p,
      commission_percent: Number(p.commission_percent),
      phone_number: (p as any).phone_number || "",
      profile_image_url: (p as any).profile_image_url || "",
      location: (p as any).location || "",
      role: roleMap.get(p.user_id) || "staff",
    }));
}

export async function createUser(email: string, password: string, username: string, role: AppRole, commissionAmount: number = 0, phoneNumber: string = "", location: string = "") {
  // Use edge function to create user without affecting current session
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-admin-user`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ email, password, username, role, commission_amount: commissionAmount, phone_number: phoneNumber, location }),
    }
  );

  const result = await res.json();
  if (!res.ok || result.error) {
    return { error: new Error(result.error || "Failed to create user") };
  }

  return { data: result, error: null };
}

export async function updateUserRole(userId: string, role: AppRole) {
  // Upsert role
  const { error: delErr } = await supabase.from("user_roles").delete().eq("user_id", userId);
  if (delErr) console.error(delErr);
  const { error } = await supabase.from("user_roles").insert({ user_id: userId, role });
  return { error };
}

export async function updateUserCommission(userId: string, amount: number) {
  const { data, error } = await supabase.rpc("admin_update_commission", {
    _target_user_id: userId,
    _commission_amount: amount,
  });
  return { data, error };
}

export async function adminResetPassword(targetUserId: string, newPassword: string) {
  const { data, error } = await supabase.rpc("admin_update_user_password", {
    _target_user_id: targetUserId,
    _new_password: newPassword,
  });
  return { data, error };
}

export async function deleteUserProfile(userId: string) {
  // Delete from profiles (cascade will handle user_roles)
  const { error } = await supabase.from("profiles").delete().eq("user_id", userId);
  return { error };
}

export async function adminUpdatePhone(userId: string, phone: string) {
  const { data, error } = await supabase.rpc("admin_update_phone" as any, {
    _target_user_id: userId,
    _new_phone: phone,
  });
  return { data, error };
}

/**
 * Fetch the auth.users email for a list of profile user_ids by invoking the
 * admin edge function. Returns a map { user_id: email }. Used by the user-
 * management UI to pre-fill the email field when editing an existing user.
 */
export async function fetchUserEmails(userIds: string[]): Promise<Record<string, string>> {
  if (!userIds.length) return {};
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return {};
  try {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-admin-user`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ action: "list_emails", user_ids: userIds }),
      }
    );
    const result = await res.json();
    if (!res.ok) return {};
    return (result?.emails || {}) as Record<string, string>;
  } catch {
    return {};
  }
}
